import { Info, PauseCircleOutline, TimerOutlined } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import isThisHour from 'date-fns/isThisHour'
import isNil from 'lodash/isNil'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { useRequestDetail } from '../../../useRequestDetail'
import { useGetPauseDetail } from '../paused-banner/useGetPauseDetail'

export interface Props {
  requestId: string
}

const POLL_INTERVAL = 2000

export const EstimatedCompletion = ({
  estimatedCompletion,
  isDueDateNow,
  avgDuration,
}: {
  estimatedCompletion?: string
  avgDuration?: number
  isDueDateNow: boolean
}) => {
  const { t } = useTranslation('requests')
  const { formatDate } = useFormatter()

  const doesEstimatedCompletionExist = !isNil(estimatedCompletion)

  return (
    <Stack
      direction="row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        color: 'neutralDark.500',
      }}
    >
      <Box sx={{ width: 16, height: 16, mr: 2 }}>
        <TimerOutlined sx={{ width: 18, height: 18 }} />
      </Box>
      <Typography variant="Body14pxRegular">
        {avgDuration ? t('avg-request-duration') : t('estimated-completion-date')}:
      </Typography>
      <>
        {!avgDuration && (
          <Typography sx={{ ml: 1, textTransform: 'capitalize' }} variant="Body14pxMedium">
            {doesEstimatedCompletionExist
              ? formatDate(new Date(estimatedCompletion), { format: 'medium' })
              : t('estimated-completion-date-not-set')}
          </Typography>
        )}
        {avgDuration && (
          <Typography sx={{ ml: 1, textTransform: 'capitalize' }} variant="Body14pxMedium">
            {t('avg-request-duration-days', { days: avgDuration })}
          </Typography>
        )}
        {(isDueDateNow || (!doesEstimatedCompletionExist && !avgDuration)) && (
          <Tooltip
            title={t(doesEstimatedCompletionExist ? 'action-required-for-last-step' : 'estimated-completion-date-not-set-info')}
          >
            <Info sx={{ color: 'neutralLight.700', fontSize: 16, ml: 1 }} />
          </Tooltip>
        )}
      </>
    </Stack>
  )
}

const Paused = ({ requestId }: Props) => {
  const { t } = useTranslation('requests')
  const { data } = useGetPauseDetail(requestId)
  const pause = data?.pause

  return (
    <Tooltip title={pause?.pause_reason.text}>
      <Stack
        direction="row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'neutralDark.500',
        }}
      >
        <Box sx={{ width: 16, height: 16, mr: 2 }}>
          <PauseCircleOutline sx={{ width: 18, height: 18 }} />
        </Box>
        <Typography variant="Body14pxRegular">{t('paused')}</Typography>
      </Stack>
    </Tooltip>
  )
}

export function EstimatedCompletionBanner({ requestId }: Props) {
  const [isPolling, setIsPolling] = useState(true)
  const { data: request } = useRequestDetail(requestId, isPolling && POLL_INTERVAL)

  const estimatedCompletion = request?.derivedFields?.estimatedCompletionDate
  const isDueDateNow = estimatedCompletion && isThisHour(new Date(estimatedCompletion))
  const avgDuration = request?.rawData.expectedSLA || undefined

  if (!request) {
    return null
  }

  if (request?.derivedFields?.isPaused) {
    return <Paused requestId={requestId} />
  }

  if (!isNil(estimatedCompletion) && isPolling) {
    setIsPolling(false)
  }

  return <EstimatedCompletion estimatedCompletion={estimatedCompletion} isDueDateNow={!!isDueDateNow} avgDuration={avgDuration} />
}
